import '../scss/components/communities-slider.scss';

/**
 * Community Slider Component
 * Handles the interactive slider for community images and sponsor logos
 */
class CommunitySlider {
    constructor() {
        this.slider = document.getElementById('community-slider');
        this.sponsorSlider = document.getElementById('sponsor-slider');
        this.currentSlide = 0;
        this.currentSponsorSlide = 0;
        this.autoplayInterval = null;
        this.sponsorAutoplayInterval = null;
        this.isHeroSlider = false;
        
        if (this.slider) {
            // Check if this is the hero slider
            this.isHeroSlider = this.slider.classList.contains('hero-community-slider');
            this.init();
        }
        
        if (this.sponsorSlider) {
            this.initSponsorSlider();
        }
    }

    init() {
        this.createSliderHTML();
        this.bindEvents();
        this.startAutoplay();
    }

    initSponsorSlider() {
        this.createSponsorSliderHTML();
        this.bindSponsorEvents();
        this.startSponsorAutoplay();
    }

    createSliderHTML() {
        const images = window.communitySliderData?.slider_images || [];
        
        if (images.length === 0) {
            this.slider.innerHTML = '<p>No community images available.</p>';
            return;
        }

        const sliderHTML = `
            <div class="community-slider-container">
                <div class="community-slider-track" data-slides="${images.length}">
                    ${images.map((imageData, index) => `
                        <div class="community-slide ${index === 0 ? 'active' : ''}" data-slide="${index}">
                            <img src="${imageData.url}" alt="${imageData.alt || `Community Image ${index + 1}`}" loading="lazy" width="${imageData.width || ''}" height="${imageData.height || ''}">
                        </div>
                    `).join('')}
                </div>
                
                ${images.length > 1 ? `
                    <div class="community-slider-controls">
                        <button class="slider-btn slider-prev" aria-label="Previous slide">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <button class="slider-btn slider-next" aria-label="Next slide">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                    
                    <div class="community-slider-dots">
                        ${images.map((_, index) => `
                            <button class="slider-dot ${index === 0 ? 'active' : ''}" data-slide="${index}" aria-label="Go to slide ${index + 1}"></button>
                        `).join('')}
                    </div>
                ` : ''}
            </div>
        `;

        this.slider.innerHTML = sliderHTML;
        // No need to call loadImages() since we have the URLs already
    }

    createSponsorSliderHTML() {
        const sponsors = window.communitySliderData?.sponsor_images || [];
        
        if (sponsors.length === 0) {
            this.sponsorSlider.innerHTML = '<p>No sponsor logos available.</p>';
            return;
        }

        const sponsorHTML = `
            <div class="sponsor-slider-container">
                <div class="sponsor-slider-track" data-slides="${sponsors.length}">
                    ${sponsors.map((imageData, index) => `
                        <div class="sponsor-slide ${index === 0 ? 'active' : ''}" data-slide="${index}">
                            <img src="${imageData.url}" alt="${imageData.alt || `Sponsor Logo ${index + 1}`}" loading="lazy" width="${imageData.width || ''}" height="${imageData.height || ''}">
                        </div>
                    `).join('')}
                </div>
                
                ${sponsors.length > 1 ? `
                    <div class="sponsor-slider-controls">
                        <button class="sponsor-btn sponsor-prev" aria-label="Previous sponsor">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <button class="sponsor-btn sponsor-next" aria-label="Next sponsor">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                ` : ''}
            </div>
        `;

        this.sponsorSlider.innerHTML = sponsorHTML;
        // No need to call loadSponsorImages() since we have the URLs already
    }

    bindEvents() {
        const prevBtn = this.slider.querySelector('.slider-prev');
        const nextBtn = this.slider.querySelector('.slider-next');
        const dots = this.slider.querySelectorAll('.slider-dot');

        if (prevBtn) prevBtn.addEventListener('click', () => this.prevSlide());
        if (nextBtn) nextBtn.addEventListener('click', () => this.nextSlide());

        dots.forEach(dot => {
            dot.addEventListener('click', (e) => {
                const slideIndex = parseInt(e.target.dataset.slide);
                this.goToSlide(slideIndex);
            });
        });

        // Pause autoplay on hover
        this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.slider.addEventListener('mouseleave', () => this.startAutoplay());

        // Touch/swipe support
        this.addTouchSupport();
    }

    bindSponsorEvents() {
        const prevBtn = this.sponsorSlider.querySelector('.sponsor-prev');
        const nextBtn = this.sponsorSlider.querySelector('.sponsor-next');

        if (prevBtn) prevBtn.addEventListener('click', () => this.prevSponsorSlide());
        if (nextBtn) nextBtn.addEventListener('click', () => this.nextSponsorSlide());

        // Pause autoplay on hover
        this.sponsorSlider.addEventListener('mouseenter', () => this.pauseSponsorAutoplay());
        this.sponsorSlider.addEventListener('mouseleave', () => this.startSponsorAutoplay());
    }

    addTouchSupport() {
        let startX = 0;
        let endX = 0;

        this.slider.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
        });

        this.slider.addEventListener('touchend', (e) => {
            endX = e.changedTouches[0].clientX;
            const diff = startX - endX;

            if (Math.abs(diff) > 50) { // Minimum swipe distance
                if (diff > 0) {
                    this.nextSlide();
                } else {
                    this.prevSlide();
                }
            }
        });
    }

    nextSlide() {
        const totalSlides = this.slider.querySelectorAll('.community-slide').length;
        this.currentSlide = (this.currentSlide + 1) % totalSlides;
        this.updateSlider();
    }

    prevSlide() {
        const totalSlides = this.slider.querySelectorAll('.community-slide').length;
        this.currentSlide = this.currentSlide === 0 ? totalSlides - 1 : this.currentSlide - 1;
        this.updateSlider();
    }

    goToSlide(index) {
        this.currentSlide = index;
        this.updateSlider();
    }

    nextSponsorSlide() {
        const totalSlides = this.sponsorSlider.querySelectorAll('.sponsor-slide').length;
        this.currentSponsorSlide = (this.currentSponsorSlide + 1) % totalSlides;
        this.updateSponsorSlider();
    }

    prevSponsorSlide() {
        const totalSlides = this.sponsorSlider.querySelectorAll('.sponsor-slide').length;
        this.currentSponsorSlide = this.currentSponsorSlide === 0 ? totalSlides - 1 : this.currentSponsorSlide - 1;
        this.updateSponsorSlider();
    }

    updateSlider() {
        const slides = this.slider.querySelectorAll('.community-slide');
        const dots = this.slider.querySelectorAll('.slider-dot');

        slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === this.currentSlide);
        });

        dots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentSlide);
        });
    }

    updateSponsorSlider() {
        const slides = this.sponsorSlider.querySelectorAll('.sponsor-slide');

        slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === this.currentSponsorSlide);
        });
    }

    startAutoplay() {
        this.pauseAutoplay();
        const totalSlides = this.slider.querySelectorAll('.community-slide').length;
        
        if (totalSlides > 1) {
            // Slower autoplay for hero slider (7 seconds vs 5 seconds)
            const interval = this.isHeroSlider ? 7000 : 5000;
            this.autoplayInterval = setInterval(() => {
                this.nextSlide();
            }, interval);
        }
    }

    pauseAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    startSponsorAutoplay() {
        this.pauseSponsorAutoplay();
        const totalSlides = this.sponsorSlider.querySelectorAll('.sponsor-slide').length;
        
        if (totalSlides > 1) {
            this.sponsorAutoplayInterval = setInterval(() => {
                this.nextSponsorSlide();
            }, 3000); // Change sponsor every 3 seconds
        }
    }

    pauseSponsorAutoplay() {
        if (this.sponsorAutoplayInterval) {
            clearInterval(this.sponsorAutoplayInterval);
            this.sponsorAutoplayInterval = null;
        }
    }

    // Public methods for external control
    destroy() {
        this.pauseAutoplay();
        this.pauseSponsorAutoplay();
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.communitySlider = new CommunitySlider();
});

// Handle page navigation (for SPAs)
document.addEventListener('navigate', () => {
    if (window.communitySlider) {
        window.communitySlider.destroy();
    }
    window.communitySlider = new CommunitySlider();
});
